import { SfxToggle, useSfx } from '@slingu/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { SENTENCES, type SentencePair } from './sentences.js';

const HIGH_SCORE_ID = 'word-builder';

export type WordBuilderProps = {
  onExit: (result: { score: number; correct: number; total: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

type Chip = {
  id: string;
  word: string;
};

const XP_CORRECT = 20;
const XP_PARTIAL = 8; // even on retry, some reward for trying
const MAX_MISTAKES = 3;
const ROUND_COUNT = 10;

/**
 * Word Builder — ordena as palavras em inglês pra formar a tradução da
 * frase em português. Toca nas palavras disponíveis pra encaixar; toca em
 * uma já colocada pra devolver. Confirma e recebe feedback. 3 erros = fim.
 *
 * Testa gramática e ordem das palavras (o ponto-fraco mais comum em quem
 * aprende inglês via vocabulário isolado).
 */
export function WordBuilder({ onExit }: WordBuilderProps) {
  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;

  const [phase, setPhase] = useState<Phase>('menu');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);

  function finishGame() {
    const result = commitScore(HIGH_SCORE_ID, score);
    setHighScore(result.best);
    setNewRecord(result.isNew);
    setPhase('over');
  }
  const [correct, setCorrect] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [queueIdx, setQueueIdx] = useState(0);
  const [queue, setQueue] = useState<SentencePair[]>([]);

  const [bank, setBank] = useState<Chip[]>([]);
  const [built, setBuilt] = useState<Chip[]>([]);
  const [feedback, setFeedback] = useState<null | {
    kind: 'correct' | 'wrong';
    expected: string;
  }>(null);

  const current = queue[queueIdx];

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  function pickRoundSentences(): SentencePair[] {
    // Pick a mix: roughly 40% L1, 40% L2, 20% L3, shuffled, then take N
    const l1 = shuffle(SENTENCES.filter((s) => s.level === 1)).slice(0, 4);
    const l2 = shuffle(SENTENCES.filter((s) => s.level === 2)).slice(0, 4);
    const l3 = shuffle(SENTENCES.filter((s) => s.level === 3)).slice(0, 2);
    return [...l1, ...l2, ...l3].slice(0, ROUND_COUNT);
  }

  function loadSentence(s: SentencePair) {
    const words = s.en.split(/\s+/).filter(Boolean);
    const chips = words.map((w, i) => ({ id: `${i}-${w}-${Math.random().toString(36).slice(2, 7)}`, word: w }));
    setBank(shuffle(chips));
    setBuilt([]);
    setFeedback(null);
  }

  function startGame() {
    const q = pickRoundSentences();
    setQueue(q);
    setQueueIdx(0);
    setScore(0);
    setCorrect(0);
    setMistakes(0);
    if (q[0]) loadSentence(q[0]);
    setPhase('playing');
    sfxRef.current.play('start');
  }

  function pickFromBank(chip: Chip) {
    if (feedback) return;
    sfxRef.current.play('tap');
    setBank((b) => b.filter((c) => c.id !== chip.id));
    setBuilt((b) => [...b, chip]);
  }

  function returnToBank(chip: Chip) {
    if (feedback) return;
    sfxRef.current.play('tap');
    setBuilt((b) => b.filter((c) => c.id !== chip.id));
    setBank((b) => [...b, chip]);
  }

  function normalize(s: string) {
    return s.toLowerCase().replace(/[.,!?;]/g, '').trim().replace(/\s+/g, ' ');
  }

  function submit() {
    if (!current) return;
    if (built.length === 0) return;
    const attempt = built.map((c) => c.word).join(' ');
    const expected = current.en;
    const isRight = normalize(attempt) === normalize(expected);
    if (isRight) {
      sfxRef.current.play('correct');
      sfxRef.current.play('xp-gain');
      setScore((s) => s + XP_CORRECT);
      setCorrect((c) => c + 1);
      setFeedback({ kind: 'correct', expected });
      window.setTimeout(() => nextSentence(), 900);
    } else {
      sfxRef.current.play('wrong');
      setMistakes((m) => m + 1);
      setScore((s) => s + XP_PARTIAL);
      setFeedback({ kind: 'wrong', expected });
      window.setTimeout(() => {
        if (mistakes + 1 >= MAX_MISTAKES) {
          finishGame();
          sfxRef.current.play('game-over');
        } else {
          nextSentence();
        }
      }, 1600);
    }
  }

  function nextSentence() {
    const next = queueIdx + 1;
    if (next >= queue.length) {
      finishGame();
      sfxRef.current.play('level-up');
      return;
    }
    setQueueIdx(next);
    if (queue[next]) loadSentence(queue[next]!);
  }

  const progress = useMemo(() => {
    if (queue.length === 0) return 0;
    return Math.floor(((queueIdx + (feedback ? 1 : 0)) / queue.length) * 100);
  }, [queueIdx, queue.length, feedback]);

  useEffect(() => {
    // nothing to subscribe
  }, []);

  return (
    <div className="sl-minigame sl-minigame--portrait sl-wbuilder__wrap">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExit({ score, correct, total: queue.length, xp: score })}
        aria-label="Fechar jogo"
      >
        ×
      </button>
      <div className="sl-flappy__sfx">
        <SfxToggle variant="icon" />
      </div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2>
            <span aria-hidden>🧩</span> Word Builder
          </h2>
          <p>
            Monte a frase em inglês na ordem correta. Toque nas palavras de
            baixo pra encaixar em cima. Toque numa palavra colocada pra
            devolver. <strong>3 erros = fim</strong>.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <button
            type="button"
            className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => { setNewRecord(false); startGame(); }}
          >
            Começar
          </button>
        </div>
      ) : null}

      {phase === 'playing' && current ? (
        <div className="sl-wbuilder">
          <div className="sl-wbuilder__hud">
            <span className="sl-wbuilder__hud-item">
              {queueIdx + 1}/{queue.length}
            </span>
            <span className="sl-wbuilder__hud-item">
              ♥ {MAX_MISTAKES - mistakes}
            </span>
            <span className="sl-wbuilder__hud-item sl-wbuilder__hud-item--xp">
              {score} XP
            </span>
          </div>

          <div className="sl-wbuilder__progress" aria-hidden>
            <span
              className="sl-wbuilder__progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="sl-wbuilder__prompt">
            <span className="sl-wbuilder__prompt-eyebrow">
              Nível {current.level}/3 · Traduza
            </span>
            <p className="sl-wbuilder__prompt-text">{current.pt}</p>
          </div>

          <div
            className={`sl-wbuilder__slot ${
              feedback?.kind === 'correct' ? 'sl-wbuilder__slot--ok' : ''
            } ${feedback?.kind === 'wrong' ? 'sl-wbuilder__slot--bad' : ''}`}
          >
            {built.length === 0 ? (
              <span className="sl-wbuilder__slot-placeholder">
                Toque nas palavras abaixo
              </span>
            ) : (
              built.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="sl-wbuilder__chip sl-wbuilder__chip--placed"
                  onClick={() => returnToBank(c)}
                  disabled={!!feedback}
                >
                  {c.word}
                </button>
              ))
            )}
          </div>

          {feedback ? (
            <p
              className={`sl-wbuilder__feedback sl-wbuilder__feedback--${feedback.kind}`}
            >
              {feedback.kind === 'correct'
                ? 'Isso aí! Mandou bem.'
                : `Ops. Correto: "${feedback.expected}"`}
            </p>
          ) : null}

          <div className="sl-wbuilder__bank">
            {bank.map((c) => (
              <button
                key={c.id}
                type="button"
                className="sl-wbuilder__chip"
                onClick={() => pickFromBank(c)}
                disabled={!!feedback}
              >
                {c.word}
              </button>
            ))}
          </div>

          <div className="sl-wbuilder__actions">
            <button
              type="button"
              className="sl-btn sl-btn--ghost"
              onClick={() => {
                sfxRef.current.play('tap');
                // return all placed chips to bank
                setBank((b) => [...b, ...built]);
                setBuilt([]);
              }}
              disabled={!!feedback || built.length === 0}
            >
              Limpar
            </button>
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--upper"
              onClick={submit}
              disabled={!!feedback || built.length === 0}
            >
              Confirmar
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : correct >= ROUND_COUNT * 0.7 ? 'Fluente em formação!' : 'Segue treinando.'}</h2>
          <p>
            Acertos: <strong>{correct}</strong>/{queue.length} · XP:{' '}
            <strong>{score}</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button
            type="button"
            className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() =>
              onExit({ score, correct, total: queue.length, xp: score })
            }
          >
            Voltar pro app
          </button>
        </div>
      ) : null}
    </div>
  );
}
